/*
  One sub-grade gauge, drawn as the office portal draws it (card-grading-admin-ui
  ScoreIndicator): a half-ring from 9 o'clock over the top to 3 o'clock, filled score/10 of
  the way, with a thin outline track either side of it. Gold at 9.5 and 10; below that,
  the final-grade tile's silver (batch 4, D8, Robert: "make it the silver that is in the
  final box to be used for both sub grades under 9"). The ring takes the tile's gradient,
  --pcg-silver-ink flat to 55% and then down to --pcg-silver-ink-lo, which is the tile
  silver itself in dark mode and the same hue darkened in light mode, where tile silver
  would vanish on white.

  The dash offset is computed in render rather than in an effect, as the portal does: there
  is nothing to measure, the radius is fixed by the viewBox.

  Sized against the Final Grade tile beside it (Robert, 2026-10-21: the arcs looked small next
  to it), then taken down 10% (Robert, 2026-10-26: "decrease the sub grades by 10% they look
  just a little too big"). The width follows the viewport up to 140px at 1920; the floor is
  58px, and all four sit in one row on a 390px phone.

  The figure is sized to the arc's opening rather than to the label below it (Robert,
  2026-10-21: "make it slightly bigger to make look better"): about 28% of the arc's width,
  so 34.6px at 1536, 41.8px at 1920, 19.4px at 390. Lifted 4% off the base so the digits
  sit on the opening's visual centre, not on the line closing the track. The title under it
  shrank by the same 10%, except at its 0.7rem floor, which a phone needs to stay legible.
*/

import { formatGrade, isGoldSubGrade } from 'lib/gradeReport';
import { useSvgId } from 'lib/useSvgId';

const RADIUS = 70;
const CIRCUMFERENCE = RADIUS * 2 * Math.PI;

/* The circle starts at 3 o'clock; only its top half is inside the viewBox. */
const dashOffsetFor = (score: number): number => ((10 - score) * CIRCUMFERENCE) / 20;

/*
  The outline track: one closed half-annulus, r=74 outside and r=66 inside, joined across
  the bottom at both ends. It used to be two open arcs, which left the unfilled tail of a
  9.5 with no line closing it off at the base (Robert, 2026-10-21).
*/
const TRACK = `M 6 80 A 74 74 0 0 1 154 80 L 146 80 A 66 66 0 0 0 14 80 Z`;

/* Read through the variables in style: a presentation attribute does not resolve var(). */
const SILVER_STOP_HI = { stopColor: 'rgb(var(--pcg-silver-ink))' };
const SILVER_STOP_LO = { stopColor: 'rgb(var(--pcg-silver-ink-lo))' };
const SILVER_TRACK = { stroke: 'rgb(var(--pcg-silver-ink-lo))' };
const SILVER_FIGURE = { color: 'rgb(var(--pcg-silver-ink))' };

type ScoreArcProps = {
  title: string;
  value: number;
};

function ScoreArc({ title, value }: ScoreArcProps) {
  const gradientId = useSvgId('pcg-arc-silver');
  const gold = isGoldSubGrade(Number(value));
  const score = Math.max(0, Math.min(10, Number(value)));

  return (
    <div className="flex w-[clamp(58px,7.92vw,140px)] flex-col items-center">
      <div className="relative w-full">
        {/* The figure below says what the arc does, so the arc is hidden from AT. */}
        {/* 81 tall, not 80, so the 1-unit line closing the base is not clipped in half. */}
        <svg aria-hidden="true" viewBox="0 0 160 81" className="block h-auto w-full">
          {!gold && (
            <defs>
              <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="0" y1="6" x2="0" y2="80">
                <stop offset="0" style={SILVER_STOP_HI} />
                <stop offset="0.55" style={SILVER_STOP_HI} />
                <stop offset="1" style={SILVER_STOP_LO} />
              </linearGradient>
            </defs>
          )}
          <path
            d={TRACK}
            className={gold ? 'stroke-pcg-goldDark' : undefined}
            style={gold ? undefined : SILVER_TRACK}
            strokeWidth={1}
            fill="none"
            strokeLinejoin="miter"
          />
          <circle
            className={gold ? 'stroke-pcg-goldDark' : undefined}
            stroke={gold ? undefined : `url(#${gradientId})`}
            cx={80}
            cy={80}
            r={RADIUS}
            strokeWidth={8}
            fill="transparent"
            strokeDasharray={`${CIRCUMFERENCE} ${CIRCUMFERENCE}`}
            strokeDashoffset={dashOffsetFor(score)}
          />
        </svg>
        <div
          className={`absolute inset-x-0 bottom-[4%] text-center font-display text-[clamp(1.215rem,2.25vw,2.61rem)] font-bold leading-none tabular-nums ${
            gold ? 'text-pcg-goldText' : ''
          }`}
          style={gold ? undefined : SILVER_FIGURE}
        >
          {formatGrade(value)}
        </div>
      </div>
      <div className="mt-1 text-[clamp(0.7rem,0.855vw,0.855rem)] font-bold uppercase tracking-wide text-pcg-textMuted">
        {title}
      </div>
    </div>
  );
}

export default ScoreArc;
